import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import SummaryHeader from "../Header/SummaryHeader";
import ProjectSummaryDefitions from "../Defintions/ProjectSummaryDefitions";
import { setProjectDetails, setProjectsData } from "../userSlice";
import { setSession, getSession } from "../utils/session";

const ProjectSummary = () => {
  const email = useSelector((state) => state.user.email);
  const nameBeforeAt = email?.split("@")[0] || "";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedOrg, setSelectedOrg] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);

  const [org, setOrg] = useState([]);
  const [status, setStatus] = useState([]);
  const [project, setProject] = useState([]);
  const [projectData, setProjectData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [fetchDone, setFetchDone] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProjects = projectData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(projectData.length / itemsPerPage);

  // RESTORE SESSION
  useEffect(() => {
    if (!email) {
      const session = getSession();
      if (session?.email) {
        dispatch(setProjectDetails({
          email: session.email,
          projectName: session.projectName,
          projectId: session.projectId,
          organizationName: session.organizationName,
          roleId: session.roleId || null,
          viewIds: session.viewIds || [],
        }));
      } else navigate("/");
    }
  }, [email, dispatch, navigate]);

  const getQueryParams = () => {
    const params = new URLSearchParams();
    selectedProject.forEach((p) => params.append("project_name", p));
    selectedOrg.forEach((o) => params.append("organization_name", o));
    selectedStatus.forEach((st) => params.append("project_status", st));
    const session = getSession();
    const userEmail = email || session?.email;
    if (userEmail) params.append("userEmail", userEmail);
    return params.toString();
  };

  // FILTER APIs
  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-org/?${getQueryParams()}`)
      .then((r) => r.json()).then((d) => setOrg(d.organization_name || [])).catch(console.error);
  }, [selectedProject, selectedStatus]);

  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-status/?${getQueryParams()}`)
      .then((r) => r.json()).then((d) => setStatus(d.project_status || [])).catch(console.error);
  }, [selectedOrg, selectedProject]);

  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-projects/?${getQueryParams()}`)
      .then((r) => r.json()).then((d) => setProject(d.project_name || [])).catch(console.error);
  }, [selectedOrg, selectedStatus]);

  // FETCH PROJECTS
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const queryParams = getQueryParams();
        const res = await fetch(`https://api-project360-insights.haqdarshak.com/api/project-summary/?${queryParams}`);
        const data = await res.json();

        const rawList = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
            ? data.results
            : [];

        const normalised = rawList.map((item) => ({
          ...item,
          pid: (item.pid ?? item.PID ?? "").trim(),
        }));

        const uniqueProjects = Array.from(
          new Map(normalised.map((item) => [item.project_name, item])).values()
        );

        console.log("[ProjectSummary] fetched projects:", uniqueProjects);

        setProjectData(uniqueProjects);
        dispatch(setProjectsData(uniqueProjects));
        setCurrentPage(1); // Reset to first page on new data

        // Auto-select if only one project
        if (uniqueProjects.length === 1) {
          const proj = uniqueProjects[0];
          const pid = proj.pid;
          const viewIds = proj.views_list || [];
          const roleId = proj.role_ids?.[0] || null;

          dispatch(setProjectDetails({
            email, projectName: proj.project_name, projectId: pid,
            organizationName: proj.organization_name, roleId, viewIds,
          }));

          setSession({
            email,
            projectName: proj.project_name,
            projectId: pid,
            organizationName: proj.organization_name,
            roleId,
            viewIds,
          });
        }
      } catch (err) {
        console.error("[ProjectSummary] fetch error:", err);
      } finally {
        setLoading(false);
        setFetchDone(true);
      }
    };

    fetchProjects();
  }, [selectedOrg, selectedStatus, selectedProject, email]);

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder }) => (
    <Select
      isMulti
      options={options.map((opt) => ({ label: opt, value: opt }))}
      value={selected.map((val) => ({ label: val, value: val }))}
      onChange={(opts) => setSelected(opts ? opts.map((o) => o.value) : [])}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      className="react-select-container"
      classNamePrefix="react-select"
      styles={{
        control: (base) => ({
          ...base,
          borderRadius: '0.5rem',
          borderColor: '#e2e8f0',
          '&:hover': { borderColor: '#cbd5e1' }
        })
      }}
    />
  );

  const handleViewDetails = (proj) => {
    const pid = proj.pid;
    const viewIds = proj.views_list || [];
    const roleId = proj.role_ids?.[0] || null;

    dispatch(setProjectDetails({
      email, projectName: proj.project_name, projectId: pid,
      organizationName: proj.organization_name, roleId, viewIds,
    }));

    setSession({
      email,
      projectName: proj.project_name,
      projectId: pid,
      organizationName: proj.organization_name,
      roleId,
      viewIds,
    });

    navigate("/Project-Level");
  };

  const clearValue = () => {
    setSelectedOrg([]);
    setSelectedProject([]);
    setSelectedStatus([]);
  };

  const handleExport = () => {
    if (!projectData.length) return alert("No data to export");
    
    // Define the fields we want to export
    const exportFields = [
      'pid',
      'organization_name',
      'project_start_date',
      'project_end_date',
      'state',
      'project_status'
    ];
    
    // Prepare CSV headers (formatted nicely)
    const headers = [
      'Project ID',
      'Organization Name',
      'Start Date',
      'End Date',
      'State',
      'Status'
    ];
    
    // Map the data to include only selected fields
    const exportData = projectData.map(project => ({
      pid: project.pid || '',
      organization_name: project.organization_name || '',
      project_start_date: project.project_start_date || '',
      project_end_date: project.project_end_date || '',
      state: project.state || '',
      project_status: project.project_status || ''
    }));
    
    // Create CSV content
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    for (const row of exportData) {
      const values = exportFields.map(field => {
        const value = row[field]?.toString() || '';
        // Escape quotes and wrap in quotes if contains comma
        const escaped = value.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.href = url;
    link.setAttribute('download', `Project_Summary_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (fetchDone && !loading && projectData.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-red-100 rounded-full p-6 mb-4 animate-pulse">
          <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 17h.01" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
        <p className="text-gray-500 text-center max-w-sm">
          You don't have access to any projects. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <SummaryHeader />

      <div className="mt-20 px-6 mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Welcome back, {nameBeforeAt}!
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2.5 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
            <ProjectSummaryDefitions />
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-8 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MultiSelectDropdown
              options={project}
              selected={selectedProject}
              setSelected={setSelectedProject}
              placeholder="Filter by Project..."
            />

            <MultiSelectDropdown
              options={org}
              selected={selectedOrg}
              setSelected={setSelectedOrg}
              placeholder="Filter by State-Org Name..."
            />
            
            <MultiSelectDropdown
              options={status}
              selected={selectedStatus}
              setSelected={setSelectedStatus}
              placeholder="Filter by Status..."
            />
            <button
              onClick={clearValue}
              className="bg-gray-200 rounded hover:bg-gray-300 transition disabled:opacity-50 shadow-md hover:shadow-lg font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Count */}
        {/* {!loading && projectData.length > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, projectData.length)} of {projectData.length} projects
          </div>
        )} */}

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-lg font-medium text-gray-700">Loading Projects...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentProjects.map((proj) => (
                <div
                  key={proj.pid}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 hover:scale-[1.02]"
                >
                  {/* Card Header with gradient accent */}
                  <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600"></div>

                  {/* Card Content */}
                  <div className="p-5">
                    {/* Project ID Badge */}
                    <div className="mb-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        PID: {proj.pid}
                      </span>
                    </div>

                    {/* Project Name */}
                    <h3 className="text-xl font-bold text-gray-800 mb-4 line-clamp-2 min-h-[3.5rem]">
                      {proj.project_name}
                    </h3>

                    {/* View Data Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(proj);
                      }}
                      className="w-full mt-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium flex items-center justify-center gap-2 group"
                    >
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      View Project Data
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Previous
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`w-10 h-10 rounded-lg transition-all duration-200 ${currentPage === pageNum
                            ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectSummary;